import computeSchema from 'frontend/_globals/compute-schema'
import { InterfaceType } from 'frontend/_setup/setupContainer/constants/InterfaceType'
import { useUnsavedColumnPresetItems } from 'frontend/columns/composables/useUnsavedColumnPresetItems'
import { ColumnPresetFields } from 'frontend/columns/config/ColumnPresetFields'
import { ColumnSortDirection } from 'frontend/columns/enum/ColumnSortDirection'
import { convertColumnWidthMapToStoredWidths } from 'frontend/columns/functions/convertColumnWidthMapToStoredWidths'
import { convertStoredWidthsToColumnWidthMap } from 'frontend/columns/functions/convertStoredWidthsToColumnWidthMap'
import { mergeColumnPresetData } from 'frontend/columns/functions/mergeColumnPresetData'
import { ColumnPresetService } from 'frontend/columns/services/ColumnPresetService'
import { IColumnPresetData } from 'frontend/columns/types/IColumnPresetData'
import { IColumnPresetResponseEntity } from 'frontend/columns/types/IColumnPresetResponseEntity'
import { IColumnSortItems } from 'frontend/columns/types/IColumnSortItems'
import { getEventOrganizationUser } from 'frontend/common/access-helpers'
import { usePinnedColumns } from 'frontend/common/composables/usePinnedColumns'
import { useSortItems } from 'frontend/common/composables/useSortItems'
import { useToggleMultiSort } from 'frontend/common/composables/useToggleMultiSort'
import { useContainer } from 'frontend/container/composables/useContainer'
import { IDataModelTypeService } from 'frontend/dataModels/services/DataModelTypeService/types/IDataModelTypeService'
import { DataModelType } from 'frontend/roles/enum/DataModelType'
import localForage from 'localforage'
import { denormalize } from 'normalizr'
import { computed, onMounted, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { intersection } from 'lodash'

export function useCustomizableTableColumns(
  dataModelType: DataModelType,
  options: {
    selectedSubtypeForColumns
    defaultDisplayedColumnNames
    availableColumns
    forceProvidedDefaultColumnsOnInit
    defaultSort
  },
) {
  const store = useStore()
  const route = useRoute()

  const { container } = useContainer()
  const { convertStoredSortToSortItems, convertSortItemsToStoredSort } = useSortItems()
  const dataModelTypeService = container.get<IDataModelTypeService>(
    InterfaceType.services.DataModelTypeService,
  )

  const columnPresetService = new ColumnPresetService()
  const columnPresets = ref<IColumnPresetResponseEntity[]>(null)
  const currentColumnPresetId = ref<string | true>(null)
  console.log('currentColumnPresetId', currentColumnPresetId.value)
  const availableColumns =
  options.availableColumns || dataModelTypeService.getColumnConfig(dataModelType) || {}
  console.log('inner availableColumns', options.availableColumns)
  const defaultColumns =
    options.defaultDisplayedColumnNames || dataModelTypeService.getDefaultColumns(dataModelType)
  const defaultVisibleColumnTypes = defaultColumns.filter(el =>
    Object.keys(availableColumns).includes(el),
  )
  if (!defaultVisibleColumnTypes.length) {
    console.debug('Provided options which cause the error to show up:', options)
    throw new Error(
      `No default columns provided for model ${dataModelType} or names don't match available columns from the config.`,
    )
  }
  const columnWidthsMap = ref<Map<string, number>>(new Map())
  const sortItems = ref<IColumnSortItems>([])
  const currentPresetName = ref<string>('')
  const currentPresetSelectedSubtypes = ref<string[]>([])
  const isMultiSortEnabled = ref<boolean>(false)
  const { toggleMultiSort } = useToggleMultiSort(sortItems, isMultiSortEnabled)
  const { pinnedColumns, updatePinnedColumns, pinnedElements, updatePinnedElements } =
    usePinnedColumns(dataModelType)
  const visibleColumnTypes = ref<string[]>(defaultVisibleColumnTypes)

  const cellConfigs = computed(() => {
    return visibleColumnTypes.value.map(columnName => {
      if (!availableColumns[columnName] || !availableColumns[columnName].cell) {
        console.warn('THERE ARE NO COLUMNS CONFIG FOR', columnName)
        return
      }
      return availableColumns[columnName].cell
    })
  })

  provide('cellConfigs', cellConfigs)

  const { unsavedColumnPresetItems, currentUnsavedColumnPresetData } = useUnsavedColumnPresetItems(
    dataModelType,
    currentColumnPresetId,
    options.selectedSubtypeForColumns,
  )

  function getPresetDataFromGlobalConfig(): IColumnPresetData {
    return {
      sortItems: [defaultVisibleColumnTypes[0], ColumnSortDirection.Ascending],
      visibleColumnTypes: defaultVisibleColumnTypes,
      columnWidthsMap: new Map(),
      pinnedColumnTypes: [],
      name: null,
    }
  }

  function getPresetDataFromColumnPreset(
    columnPreset: IColumnPresetResponseEntity,
  ): IColumnPresetData {
    if (!columnPreset) {
      return
    }
    return {
      sortItems: convertStoredSortToSortItems(columnPreset.defaultSortColumnType),
      columnWidthsMap: convertStoredWidthsToColumnWidthMap(columnPreset.columnWidths),
      visibleColumnTypes: columnPreset.enabledColumnTypes.filter(el => {
        return Object.keys(availableColumns).includes(el)
      }),
      pinnedColumnTypes: columnPreset.pinnedColumnTypes,
      name: columnPreset.name,
      dataModelSubtype: columnPreset.dataModelSubtype,
    }
  }

  function getPresetDataFromUnsavedColumnPresetItem(
    columnPresetId: string | true,
  ): IColumnPresetData {
    const unsavedColumnPresetItemData = unsavedColumnPresetItems.value.find(preset => {
      return preset.columnPresetId === columnPresetId
    })?.data
    return (
      unsavedColumnPresetItemData || {
        sortItems: null,
        columnWidthsMap: null,
        visibleColumnTypes: null,
        pinnedColumnTypes: null,
        name: null,
        dataModelSubtype: null,
      }
    )
  }

  function getDefaultColumnData(columnPreset?: IColumnPresetResponseEntity): IColumnPresetData {
    return columnPreset
      ? getPresetDataFromColumnPreset(columnPreset)
      : getPresetDataFromGlobalConfig()
  }

  const unsavedColumnPresets = computed(() => {
    return (
      columnPresets.value?.filter(columnPreset => {
        const unsavedColumnPresetItemData = unsavedColumnPresetItems.value.find(preset => {
          return preset.columnPresetId === columnPreset.id
        })?.data
        if (!unsavedColumnPresetItemData) {
          return false
        }
        return isDataDifferent(
          mergeColumnPresetData(getDefaultColumnData(columnPreset), unsavedColumnPresetItemData),
          getDefaultColumnData(columnPreset),
        )
      }) || []
    )
  })
  const unsavedColumnPresetIds = computed(() => {
    const output: (string | true)[] = unsavedColumnPresets.value.map(item => item.id)
    const unsavedColumnPresetItemData = unsavedColumnPresetItems.value.find(preset => {
      return preset.columnPresetId === true
    })?.data
    const isDefaultPresetUnsaved = unsavedColumnPresetItemData
      ? isDataDifferent(
          mergeColumnPresetData(getDefaultColumnData(null), unsavedColumnPresetItemData),
          getDefaultColumnData(null),
        )
      : false
    if (isDefaultPresetUnsaved) {
      output.push(true)
    }
    return output
  })

  const currentOrganizationUserId = computed(() => {
    return (
      store.state.session.user?.impersonatedCurrentOrganizationUser?.id ||
      store.state.session.user?.currentOrganizationUser?.id
    )
  })

  function isDataDifferent(
    data1: IColumnPresetData | Record<string, never>,
    data2: IColumnPresetData | Record<string, never>,
  ) {
    const allColumnWidthKeys = [
      Array.from(data1.columnWidthsMap.keys()),
      Array.from(data2.columnWidthsMap.keys()),
    ]
    const uniqueColumnWidthKeys = Array.from(new Set(allColumnWidthKeys.flat()))

    const areColumnWidthsEqual = uniqueColumnWidthKeys.every(key => {
      return (data1.columnWidthsMap.get(key) || null) === (data2.columnWidthsMap.get(key) || null)
    })
    const areTwoNamesEqual = data1.name === data2.name || (!data1.name && !data2.name)

    const rules = [
      data1.visibleColumnTypes?.join(',') === data2.visibleColumnTypes?.join(','),
      convertSortItemsToStoredSort(data1.sortItems) ===
        convertSortItemsToStoredSort(data2.sortItems),
      areColumnWidthsEqual,
      areTwoNamesEqual,
      data1.pinnedColumnTypes?.join(',').length === data2.pinnedColumnTypes?.join(',').length,
    ]
    return rules.some(rule => !rule)
  }

  function loadColumnPreset(columnPresetId: string | true) {
    let key = `currentColumnPresetId:${dataModelType}`
    if (options.selectedSubtypeForColumns.value?.length) {
      key += `:${options.selectedSubtypeForColumns.value}`
    }
    if (columnPresetId !== true) {
      console.log(options.selectedSubtypeForColumns.value, 'setting currentColumnPresetId', key, columnPresetId)
      localForage.setItem(key, columnPresetId).then(() => {
        localForage.getItem(key).then(v => {
          console.log('got currentColumnPresetId from storage', v)
        })
      })
    } else {
      localForage.removeItem(key)
    }
    const columnPreset = columnPresets.value.find(preset => preset.id === columnPresetId)
    const currentData = mergeColumnPresetData(
      getDefaultColumnData(columnPreset),
      getPresetDataFromUnsavedColumnPresetItem(columnPresetId) || {},
    )
    visibleColumnTypes.value = currentData.visibleColumnTypes
    sortItems.value = currentData.sortItems
    pinnedColumns.value = currentData.pinnedColumnTypes
    if (isMultiSortEnabled.value === false && currentData.sortItems.length > 1) {
      isMultiSortEnabled.value = true
    } else if (isMultiSortEnabled.value === true && currentData.sortItems.length == 1) {
      isMultiSortEnabled.value = false
    }
    columnWidthsMap.value = currentData.columnWidthsMap
    currentPresetName.value = currentData.name || null
    currentPresetSelectedSubtypes.value = currentData?.dataModelSubtype || []
  }
  watch(currentColumnPresetId, value => {
    loadColumnPreset(value)
  })

  async function fetchColumnPresets() {
    const response = await columnPresetService.getAvailableFor(dataModelType, ColumnPresetFields)
    let schema
    if (!Object.keys(response.mappings || {}).length) {
      schema = computeSchema([], {})
    } else {
      schema = computeSchema(ColumnPresetFields, response.mappings)
    }
    return response.result?.items?.map(itemId => {
      return denormalize(itemId, (schema?.items || [])[0] || schema, response.entities)
    })
  }

  watch(sortItems, value => {
    currentUnsavedColumnPresetData.value = {
      ...currentUnsavedColumnPresetData.value,
      sortItems: value,
    }
  })

  watch(columnWidthsMap, value => {
    currentUnsavedColumnPresetData.value = {
      ...currentUnsavedColumnPresetData.value,
      columnWidthsMap: value,
    }
  })

  watch(visibleColumnTypes, value => {
    currentUnsavedColumnPresetData.value = {
      ...currentUnsavedColumnPresetData.value,
      visibleColumnTypes: value,
    }
  })

  watch(currentPresetName, value => {
    currentUnsavedColumnPresetData.value = {
      ...currentUnsavedColumnPresetData.value,
      name: value,
    }
  })

  watch(currentPresetSelectedSubtypes, value => {
    console.log('got currentPresetSelectedSubtypes', value)
    currentUnsavedColumnPresetData.value = {
      ...currentUnsavedColumnPresetData.value,
      dataModelSubtype: value,
    }
  })

  watch(pinnedColumns, value => {
    currentUnsavedColumnPresetData.value = {
      ...currentUnsavedColumnPresetData.value,
      pinnedColumnTypes: value,
    }
  })

  watch(currentOrganizationUserId, newVal => {
    if (newVal) {
      fetchAndInitializeColumnPresets()
    }
  })

  const defaultColumnPresetId = ref(null)
  async function fetchAndInitializeColumnPresets() {
    if (!dataModelType) {
      return
    }
    let key = `${dataModelType}`
    if (options.selectedSubtypeForColumns.value?.length) {
      key += `:${options.selectedSubtypeForColumns.value}`
    }
    const [columnPresetsResponse, currentColumnPresetIdFromStorage] = await Promise.all([
      fetchColumnPresets(),
      localForage.getItem(`currentColumnPresetId:${key}`),
      localForage.getItem(`unsavedColumnPresetItems:${dataModelType}`),
    ])

    columnPresets.value = columnPresetsResponse

    if (columnPresets.value.length) {
      if (
        currentColumnPresetIdFromStorage &&
        !!columnPresets.value.find(el => el.id == currentColumnPresetIdFromStorage)
      ) {
        currentColumnPresetId.value = currentColumnPresetIdFromStorage as string
      } else if (currentColumnPresetIdFromStorage === null) {
        currentColumnPresetId.value = columnPresets.value.sort((a, b) => a.position - b.position)[0]?.id
        // currentColumnPresetId.value = true
      } else {
        if (options.selectedSubtypeForColumns.value?.length) {
          const selectedSubtypePreset = columnPresets.value.find(el => el.dataModelSubtype?.includes(options.selectedSubtypeForColumns.value))
          if (selectedSubtypePreset) {
            currentColumnPresetId.value = selectedSubtypePreset.id
          } else {
            currentColumnPresetId.value = columnPresets.value[0].id
          }
        } else {
          currentColumnPresetId.value = columnPresets.value[0].id
        }
      }
      defaultColumnPresetId.value = currentColumnPresetId.value
    } else {
      currentColumnPresetId.value = true
      defaultColumnPresetId.value = true
      const finalSortItems = []
      const defaultSortArray = Array.isArray(options.defaultSort)
        ? options.defaultSort
        : [options.defaultSort]
      for (const item of defaultSortArray) {
        const splittedItem = item.split(' ')
        finalSortItems.push([
          splittedItem[0],
          splittedItem[1] === 'asc'
            ? ColumnSortDirection.Ascending
            : ColumnSortDirection.Descending,
        ])
      }
      sortItems.value = finalSortItems
    }
  }

  function getDefaultColumnPresetId(columnPresets, currentColumnPresetIdFromStorage, skipSystemDefaultPreset = false) {
    let result = null
    console.log('columnPresets', columnPresets.value, 'currentColumnPresetIdFromStorage', currentColumnPresetIdFromStorage)
    if (columnPresets?.value?.length) {
      if (
        currentColumnPresetIdFromStorage &&
        !!columnPresets.value.find(el => el.id == currentColumnPresetIdFromStorage)
      ) {
        result = currentColumnPresetIdFromStorage as string
      } else if (currentColumnPresetIdFromStorage === null && !skipSystemDefaultPreset) {
        result = true
      } else {
        if (options.selectedSubtypeForColumns?.value?.length) {
          const selectedSubtypePreset = columnPresets.value.find(el => {
            return intersection(el.dataModelSubtype || [], options.selectedSubtypeForColumns.value.split(',')).length == options.selectedSubtypeForColumns.value.split(',').length
          })
          // el.dataModelSubtype?.includes(options.selectedSubtypeForColumns.value)
          if (selectedSubtypePreset) {
            result = selectedSubtypePreset.id
          } else {
            result = columnPresets.value[0].id
          }
        } else {
          result = columnPresets.value[0].id
        }
      }
    } else {
      result = true
    }
    console.log('getDefaultColumnPresetId result', result)
    return result
  }

  watch(options.selectedSubtypeForColumns, () => {
    console.log('selectedSubtypeForColumns changed in useCustomizableTableColumns', options.selectedSubtypeForColumns?.value)
    if (!dataModelType) {
      return
    }
    let key = `${dataModelType}`
    if (options.selectedSubtypeForColumns?.value?.length) {
      key += `:${options.selectedSubtypeForColumns.value}`
    }
    localForage.getItem(`currentColumnPresetId:${key}`).then(value => {
      currentColumnPresetId.value = getDefaultColumnPresetId(columnPresets, value, true)
      defaultColumnPresetId.value = currentColumnPresetId.value
      loadColumnPreset(currentColumnPresetId.value)
    })
    // const [columnPresetsResponse, currentColumnPresetIdFromStorage] = await Promise.all([
    //   fetchColumnPresets(),
    //   ,
    //   localForage.getItem(`unsavedColumnPresetItems:${dataModelType}`),
    // ])


    // if (options.selectedSubtypeForColumns?.value) {
    //   // if (defaultColumnPresetId.value == currentColumnPresetId.value) {
    //   // }
    // }
  })

  onMounted(async () => {
    if (currentOrganizationUserId.value) {
      fetchAndInitializeColumnPresets()
    }
  })

  const currentColumnPreset = computed(() => {
    return columnPresets.value?.find(preset => preset.id === currentColumnPresetId.value)
  })

  const defaultDisplayedColumnNames = computed(() => {
    return defaultColumns
  })

  const computedAvailableColumns = computed(() => {
    return availableColumns
  })

  const areColumnPresetsLoading = computed(() => {
    return !columnPresets.value
  })

  const isPresetUnsaved = computed(() => {
    return unsavedColumnPresetIds.value.includes(currentColumnPresetId.value)
  })

  async function saveCurrentColumnPreset() {
    if (!currentColumnPreset.value) {
      return
    }
    await saveColumnPreset({
      columnPresetId: currentColumnPreset.value.id,
    })
  }

  const allColumnPresetData = computed(() => {
    const items =
      columnPresets.value?.map(columnPreset => {
        const unsavedColumnPresetItemData = unsavedColumnPresetItems.value.find(preset => {
          return preset.columnPresetId === columnPreset.id
        })?.data
        return {
          columnPresetId: columnPreset.id,
          data: {
            ...getDefaultColumnData(columnPreset),
            ...(unsavedColumnPresetItemData || {}),
          },
        }
      }) || []
    items.push({
      columnPresetId: true,
      data: {
        ...getDefaultColumnData(null),
        ...(unsavedColumnPresetItems.value.find(preset => {
          return preset.columnPresetId === true
        })?.data || {}),
      },
    })
    return items
  })

  async function saveColumnPreset(params: {
    columnPresetId: string | true
    name?: string
    dataModelSubtype?: string[]
    onSuccessCallback?(): void
    onErrorCallback?(errors: Record<string, string[]>): void
  }) {
    const { columnPresetId, name, dataModelSubtype, onSuccessCallback, onErrorCallback } = params
    const columnPreset = columnPresets.value.find(preset => {
      return preset.id === columnPresetId
    })
    const columnPresetData = allColumnPresetData.value.find(item => {
      return item.columnPresetId === columnPresetId
    })?.data
    const eventOrganizationUser = getEventOrganizationUser(
      store.state.session.user,
      route.params.eventSlug,
    )

    const columnPresetToSave: {
      id?: string
      name?: string
      dataModelSubtype?: string[]
      defaultSortColumnType: string
      pinnedColumnTypes: string[]
      enabledColumnTypes: string[]
      eventOrganizationUserId: string
      dataModelType: DataModelType
      columnWidths: string
    } = {
      defaultSortColumnType: convertSortItemsToStoredSort(columnPresetData.sortItems),
      pinnedColumnTypes: columnPresetData.pinnedColumnTypes,
      enabledColumnTypes: columnPresetData.visibleColumnTypes,
      eventOrganizationUserId: eventOrganizationUser.id,
      dataModelType: dataModelType,
      columnWidths: convertColumnWidthMapToStoredWidths(columnPresetData.columnWidthsMap),
    }
    const isCurrentPresetGlobal = columnPreset?.isGlobal
    if (name || columnPresetId === true || isCurrentPresetGlobal) {
      columnPresetToSave.name = name || `New preset ${columnPresets.value.length + 1}`
    }
    if (dataModelSubtype) {
      columnPresetToSave.dataModelSubtype = dataModelSubtype
    }
    if (!(isCurrentPresetGlobal || columnPresetId === true)) {
      columnPresetToSave.id = columnPresetId
    }
    try {
      const response = await columnPresetService.save({
        data: {
          ...columnPresetToSave,
        },
      })
      unsavedColumnPresetItems.value = unsavedColumnPresetItems.value.filter(preset => {
        return preset.columnPresetId !== columnPresetId
      })
      await refetchColumnPresets()
      currentColumnPresetId.value = response.data.id
      loadColumnPreset(response.data.id)
      if (onSuccessCallback) {
        onSuccessCallback()
      }
    } catch (e) {
      if (onErrorCallback) {
        onErrorCallback(e.response?.data?.errors)
      }
    }
  }

  function restoreColumnPreset(columnPresetId: string | true) {
    unsavedColumnPresetItems.value = unsavedColumnPresetItems.value.filter(preset => {
      return preset.columnPresetId !== columnPresetId
    })
    if (columnPresetId === currentColumnPresetId.value) {
      loadColumnPreset(columnPresetId)
    }
  }

  async function refetchColumnPresets() {
    columnPresets.value = await fetchColumnPresets()
  }

  return {
    defaultDisplayedColumnNames,
    displayedColumnNames: visibleColumnTypes,
    availableColumns: computedAvailableColumns,
    columnWidthsMap,
    areColumnPresetsLoading,
    columnPresets,
    currentColumnPresetId,
    isPresetUnsaved,
    saveCurrentColumnPreset,
    sortItems,
    pinnedColumns,
    pinnedElements,
    currentPresetName,
    currentPresetSelectedSubtypes,
    isMultiSortEnabled,
    toggleSort: toggleMultiSort,
    updatePinnedColumns,
    updatePinnedElements,
    unsavedColumnPresetIds,
    saveColumnPreset,
    restoreColumnPreset,
    fetchColumnPresets: refetchColumnPresets,
  }
}
